import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import { DFPManager } from '@edmunds/react-dfp';
import { connect } from 'react-redux';
import { isNode } from 'client/utils/environment';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EdmundsAPI } from 'client/data/api/api-client';

import { EventToolbox } from 'client/utils/event-toolbox';
import { EVENTS } from 'client/utils/event-constants';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getTotalAdUnitsToBeRendered } from 'client/site-modules/shared/components/ad-unit/ad-unit';
import { getPwtEnabled, getPwtEnabledForPage } from 'client/utils/pwt/get-pwt-enabled-page';
import { getBidEligibleAdUnits } from 'client/utils/pwt/get-bid-eligible-array';
import { isHeadlessBrowser } from 'client/utils/is-headless-browser';
import { getIasData } from 'client/utils/get-ias-data';
import { getPwtBids } from 'client/utils/pwt/get-pwt-bids';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { VisitorModel } from 'client/data/models/visitor';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { getVisitorContext } from './context/visitor-context';
import { getPageContext, getWtfContext } from './context/page-context';

const empty = {};

let loadAlreadyCalled = false;
let iasAlreadyCalled = false;
let pwtAlreadyCalled = false;
let privacyChangedOnTheFly = false;
let frData = '';
let iasKeywordData = '';
let targeting = {};
const EMPTY_TARGETING = {};

const targetingSubscribers = new Set();

const RTT_TIMER_CALLS = [5000, 10000, 15000];

export const getTargeting = () => {
  return targeting?.fr ? targeting : EMPTY_TARGETING;
};

export const subscribeTargeting = callback => {
  targetingSubscribers.add(callback);
  return () => targetingSubscribers.delete(callback);
};

export const publishTargeting = nextTargeting => {
  targeting = nextTargeting || {};
  targetingSubscribers.forEach(callback => callback());
};

export const resetTest = () => {
  loadAlreadyCalled = false;
  pwtAlreadyCalled = false;
};

export const pwtHandler = {
  getBids: async () => {
    pwtAlreadyCalled = true;
    await getPwtBids(getBidEligibleAdUnits());
    DFPManager.load();
  },
  load: () => {
    if (window.PWT.isLoaded) {
      pwtHandler.getBids();
    } else {
      window.addEventListener('pwtLoaded', pwtHandler.getBids, false);
    }
  },
};

if (!isNode()) {
  DFPManager.configureSingleRequest(true);
  DFPManager.setCollapseEmptyDivs(true);
}

export class AdsContextUI extends Component {
  constructor(props) {
    super(props);
    this.rttCalls = 0;
    this.state = {};
    this.triggerLoadIfPossible = this.triggerLoadIfPossible.bind(this);
  }

  async componentDidMount() {
    EventToolbox.on(EVENTS.TRACK_ACTION, this.handleResolveRttSegment);
    // In the decorator the <AdsContext /> MUST come after the page content, so
    // by now the page has already mounted, so experiments from page are already known so all data for targetting is known
    // console.time('contextMountToDFPLoadStart');
    await this.setTargeting();
    // also, ideal if AdsContext comes after all AdUnits (sticky banner, pubpixel), that way if in future AdSlots are
    // outside the async ClientSideRender, they would be synchronous, but all mounted and registered by the time
    // reaching this line of code
    DFPManager.on('slotRegistered', this.triggerLoadIfPossible);
    // if in future ads are rendered without CSR all ads might already be registered at this point, so callback above
    // won't fire, to account for that invoke triggerLoadIfPossible() below explicitly
    this.triggerLoadIfPossible();
  }

  async componentDidUpdate(prevProps) {
    const { enableGpc, privacy } = this.props;
    if (enableGpc && loadAlreadyCalled && prevProps.privacy?.uspString !== privacy?.uspString) {
      loadAlreadyCalled = false;
      privacyChangedOnTheFly = true;
      DFPManager.on('slotRegistered', this.triggerLoadIfPossible);
    }
    await this.setTargeting();
    this.triggerLoadIfPossible();
  }

  componentWillUnmount() {
    EventToolbox.off(EVENTS.TRACK_ACTION, this.handleResolveRttSegment);
    window.removeEventListener('pwtLoaded', pwtHandler.getBids, false);
  }

  async setTargeting() {
    const {
      page,
      id,
      session,
      iasData,
      privacy,
      enableGpc,
      rttSegment,
      vehicle,
      region,
      legacy,
      dealer,
      userEnterZip,
      pageLocation,
      isCheckHeadlessBrowser,
      isEnableIAS,
      ads,
    } = this.props;

    const activeCampaigns = ExperimentUtil.debug.getVenomAssignmentsOnPage();
    const headlessBrowser = isCheckHeadlessBrowser && (await isHeadlessBrowser());
    const visitor = { id, session, iasData };

    if (iasAlreadyCalled === false) {
      iasAlreadyCalled = true;
      const iasDataValue = isEnableIAS && (await getIasData(session, iasData));
      frData = get(iasDataValue, 'fr', null);
      iasKeywordData = get(iasDataValue, 'matchedKeyword', null);
    }

    const nextTargeting = {
      ...getWtfContext(activeCampaigns),
      ...getVisitorContext(visitor, region, userEnterZip),
      ...getPageContext(
        page,
        pageLocation,
        vehicle,
        legacy,
        dealer,
        headlessBrowser,
        frData,
        ads.utmAds,
        iasKeywordData
      ),
      aseg: rttSegment || this.state.rttSegment,
    };

    DFPManager.setTargetingArguments(nextTargeting);
    publishTargeting(nextTargeting);

    if (enableGpc) {
      const doNotSell = privacy?.doNotSell;
      DFPManager.configureLimitedAds(doNotSell);
      window.googletag.cmd.push(() => {
        window.googletag.pubads().setPrivacySettings({
          restrictDataProcessing: doNotSell,
          us_privacy: privacy?.uspString,
          limitedAds: doNotSell,
        });
      });
    }
  }

  handleResolveRttSegment = async () => {
    EventToolbox.off(EVENTS.TRACK_ACTION, this.handleResolveRttSegment);
    await this.resolveRttSegment();
  };

  resolveRttSegment = async () => {
    const { disableUseRTTModel, disableRTTForE2e } = this.props;
    if (disableUseRTTModel && !disableRTTForE2e) {
      const rttSegment = await EdmundsAPI.fetchJson(`/user-db/v1/segments/${this.props.id}`, {
        retries: 0,
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache' },
      }).catch(() => []);

      this.setState({ rttSegment });

      if (this.rttCalls < RTT_TIMER_CALLS.length && !rttSegment?.length) {
        setTimeout(this.resolveRttSegment, RTT_TIMER_CALLS[this.rttCalls]);
        this.rttCalls += 1;
      }
    }
  };

  /**
   * triggers react-dfp/DFPManager.load() when
   * 1. rttSegment is retrieved - it is retrieved on client side if page disabled it for preload, or it timed out on server side
   * 2. visitor.location is retrieved - it is retrieved on client side if page disabled it for preload, or it timed out on server side
   * 3. react-dfp/AdSlots from our AdUnits have all been registered with DFPManager
   *
   * note that AdUnits are mounted synchronously but AdSlots are registered later asynchronously because they are inside of
   * ClientSideRender
   *
   * DFPManager.load() only needs to be called once during web app load. On the first call to DFPManager.load() the first
   * SRA network call will trigger.Any subsequent AdSlot coming into page will automatically be handled by DFPManager,
   * generating it's own network request.
   *
   * purpose of this function is to ensure that DFPManager.load() is called at the right time during page load - after all the
   * AdUnits on page are mounted and their corresponding AdsSlot registers() are complete, not before that - this way there
   * is only a single network call to securepubads.g.doubleclick.net/gampad/ads?... which means page is wired correctly for
   * SRA.
   *
   */
  triggerLoadIfPossible = () => {
    if (loadAlreadyCalled) {
      return;
    }
    const { rttSegment, isAuthenticated, hasUserData, enableGpc, disableUseRTTModel, disableRTTForE2e, region } =
      this.props;

    const waitingForRttSegment =
      (!disableUseRTTModel && rttSegment === null) ||
      (disableUseRTTModel && !disableRTTForE2e && !this.state.rttSegment);
    const useGpcWorkflow = enableGpc && (isAuthenticated === null || (isAuthenticated && !hasUserData));
    const waitingForLocationContext = !region;

    if (waitingForRttSegment || useGpcWorkflow || waitingForLocationContext) {
      return;
    }
    const totalAdUnitsToBeRendered = getTotalAdUnitsToBeRendered();
    const registeredSlots = DFPManager.getRegisteredSlots();
    const numRegisteredSlots = Object.keys(registeredSlots).length;

    if (numRegisteredSlots && numRegisteredSlots >= totalAdUnitsToBeRendered) {
      const { pwtEnabled, pwtEnabledForPage } = this.props;

      loadAlreadyCalled = true;
      DFPManager.removeListener('slotRegistered', this.triggerLoadIfPossible);
      /*
       * Reference client/utils/get-pwt-bids.js
       * `pwtHandlers.refresh();`
       * For refreshing of the googletag pubads
       */
      if (privacyChangedOnTheFly) {
        privacyChangedOnTheFly = false;
        if (pwtEnabledForPage) {
          setTimeout(() => {
            window.googletag.cmd.push(() => {
              window.googletag.pubads().refresh();
              window.EDM.Venom.privacyChanged = true;
            });
          });
        }
        return;
      }

      if (pwtEnabled && pwtAlreadyCalled === false && window.PWT) {
        pwtHandler.load();
      } else if (!pwtEnabled) {
        DFPManager.load();
      }
      window.EDM.Venom.adsLoadTriggered = true;
    }
  };

  render() {
    const { pageAdsList, pageAdsListVerified, pageAdsListSeparatedCalls, pageAdsListAdCalls } = this.props;

    return (
      <ErrorBoundary>
        <div
          data-ad-names={Array.from(pageAdsListSeparatedCalls || pageAdsList || []).join()}
          data-ad-model-verified={pageAdsListVerified ? Array.from(pageAdsListVerified).join() : 'verifying'}
          data-ad-model-calls={pageAdsListAdCalls}
          hidden
        />
      </ErrorBoundary>
    );
  }
}
// there is a bug in eslint - it thinks these props are not used
AdsContextUI.propTypes = {
  ads: PropTypes.shape({
    adsts: PropTypes.shape({
      value: PropTypes.number,
      trigger: PropTypes.string,
    }),
    utmAds: PropTypes.shape({}),
  }).isRequired,
  page: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
  }).isRequired,
  pageLocation: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  legacy: PropTypes.shape({
    pageName: PropTypes.string,
    categoryName: PropTypes.string,
  }).isRequired,
  vehicle: PropTypes.shape({
    type: PropTypes.object,
    make: PropTypes.object,
    model: PropTypes.object,
    modelYear: PropTypes.object,
    subModel: PropTypes.object,
    publicationState: PropTypes.string,
  }).isRequired,
  dealer: PropTypes.shape({
    rooftopId: PropTypes.string,
  }).isRequired,
  region: PropTypes.shape({
    zipCode: PropTypes.string.isRequired,
    ipZipCode: PropTypes.string,
    ipStateCode: PropTypes.string,
    dma: PropTypes.string.isRequired,
    stateCode: PropTypes.string.isRequired,
  }),
  id: PropTypes.string.isRequired,
  session: PropTypes.string.isRequired,
  iasData: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  hasUserData: PropTypes.bool,
  privacy: PropTypes.shape({
    doNotSell: PropTypes.bool,
    uspString: PropTypes.string,
  }),
  enableGpc: PropTypes.bool,
  rttSegment: PropTypes.arrayOf(PropTypes.string),
  userEnterZip: PropTypes.bool,
  isCheckHeadlessBrowser: PropTypes.bool,
  isEnableIAS: PropTypes.bool,
  pwtEnabled: PropTypes.bool,
  pwtEnabledForPage: PropTypes.bool,
  pageAdsList: PropTypes.arrayOf(PropTypes.string),
  pageAdsListVerified: PropTypes.arrayOf(PropTypes.string),
  pageAdsListSeparatedCalls: PropTypes.arrayOf(PropTypes.string),
  pageAdsListAdCalls: PropTypes.number,
  disableUseRTTModel: PropTypes.bool,
  disableRTTForE2e: PropTypes.bool,
};

AdsContextUI.defaultProps = {
  userEnterZip: false,
  isCheckHeadlessBrowser: false,
  isEnableIAS: false,
  pwtEnabled: false,
  pwtEnabledForPage: false,
  iasData: null,
  isAuthenticated: null,
  hasUserData: false,
  privacy: {},
  enableGpc: false,
  rttSegment: null,
  pageAdsList: null,
  pageAdsListVerified: null,
  pageAdsListSeparatedCalls: null,
  pageAdsListAdCalls: null,
  disableUseRTTModel: false,
  disableRTTForE2e: false,
  region: undefined,
};

/**
 * Maps store state pageContext to component properties
 *
 * @param  {Object} state Store state
 * @return {Object}       State to props mapping
 */
export const mapStateToProps = state => ({
  ads: state.pageContext.ads || empty,
  page: state.pageContext.page || empty,
  pageLocation: state.pageContext.location || empty,
  legacy: state.pageContext.legacy || empty,
  id: state.visitor?.id,
  session: state.visitor?.session,
  isAuthenticated: state.profile?.isAuthenticated,
  privacy: state.visitor?.privacy,
  enableGpc: state.featureFlags.enableGpc,
  iasData: state.visitor?.iasData,
  vehicle: state.pageContext.vehicle || empty,
  dealer: state.pageContext.dealer || empty,
  userEnterZip: state.featureFlags.userEnterZip,
  isEnableIAS: state.featureFlags.enableIAS,
  isCheckHeadlessBrowser: state.featureFlags.headlessBrowser,
  pwtEnabled: getPwtEnabled(state),
  pwtEnabledForPage: getPwtEnabledForPage(state),
  disableUseRTTModel: state.featureFlags['disable-use-rtt-model'], // disable using model
  disableRTTForE2e: state.featureFlags['disable-rtt-for-e2e'], // this flag is only used in e2e tests
});

export const stateToPropsConfig = {
  pageAdsList: bindToPath('pageAdsList', NativeAdsCreativeConfigModel),
  pageAdsListVerified: bindToPath('pageAdsListVerified', NativeAdsCreativeConfigModel),
  pageAdsListSeparatedCalls: bindToPath('pageAdsListSeparatedCalls', NativeAdsCreativeConfigModel),
  pageAdsListAdCalls: bindToPath('pageAdsListAdCalls', NativeAdsCreativeConfigModel),
  rttSegment: bindToPath('rttSegment', VisitorModel),
  hasUserData: bindToPath(
    ({ isAuthenticated }) => isAuthenticated && 'data.idm',
    UserProfileModel,
    data => !isEmpty(data)
  ),
  region: bindToPath('location', VisitorModel),
};

export const AdsContext = connect(mapStateToProps)(connectToModel(AdsContextUI, stateToPropsConfig));
